<script setup>
import { computed, inject } from 'vue';
import DOMPurify from 'isomorphic-dompurify';
import BaseBubble from './Base.vue';

const props = defineProps({
  id: { type: Number, required: true },
  content: { type: String, default: '' },
  contentAttributes: { type: Object, default: () => ({}) },
  conversationId: { type: Number, required: true },
});

// Injetar context do Message.vue
const messageContext = inject('messageContext', {});
const variant = computed(() => messageContext.variant?.value || 'user');

// Injetar as mensagens do MessageList/MessagesView
const allMessages = inject('allMessages', []);

const items = computed(() => props.contentAttributes?.items || []);

// Encontrar a mensagem que respondeu a esta (in_reply_to)
const replyMessage = computed(() => {
  const messages = Array.isArray(allMessages) ? allMessages : (allMessages.value || []);
  
  return messages.find(msg => {
    const inReplyTo = msg.content_attributes?.in_reply_to || 
                      msg.contentAttributes?.inReplyTo ||
                      msg.contentAttributes?.in_reply_to;
    return inReplyTo === props.id;
  });
});

// Valor clicado (content da mensagem de resposta)
const clickedValue = computed(() => {
  if (!replyMessage.value) return null;
  return replyMessage.value.content;
});

// Mapeamento de backgrounds PRESSIONADOS (sobrescreve o bg do BaseBubble)
const variantPressedBgMap = {
  user: 'bg-n-slate-5',
  agent: 'bg-n-blue-6',
  bot: 'bg-n-iris-6',
};

// Classes de estado do botão (elevado ou pressionado)
const getButtonStateClasses = (isClicked) => {
  if (isClicked) {
    // Sobrescrever background do BaseBubble + efeitos de pressionado
    const pressedBg = variantPressedBgMap[variant.value] || variantPressedBgMap.user;
    
    return [
      pressedBg, // Sobrescreve o bg herdado do BaseBubble
      'button-pressed',
      'ring-2 ring-offset-1 ring-n-blue-9 dark:ring-n-blue-6'
    ];
  } else {
    // Botão normal: mantém bg do BaseBubble + adiciona elevação
    return [
      'button-elevated',
      'border-2 border-n-alpha-2'
    ];
  }
};

const processMarkdown = (text) => {
  if (!text) return '';
  let processed = text;
  
  processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  processed = processed.replace(/\*(?!\s)([^*]+?)(?<!\s)\*/g, '<strong>$1</strong>');
  processed = processed.replace(/(?<![\w/])_([^_]+?)_(?![\w/])/g, '<em>$1</em>');
  processed = processed.replace(/~(.+?)~/g, '<u>$1</u>');
  processed = processed.replace(/~~(.+?)~~/g, '<s>$1</s>');
  processed = processed.replace(/`([^`]+?)`/g, '<code class="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded text-sm font-mono">$1</code>');
  processed = processed.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-woot-500 hover:underline">$1</a>');
  processed = processed.replace(/(?<!href=")https?:\/\/[^\s<]+/g, match => `<a href="${match}" target="_blank" class="text-woot-500 hover:underline break-all">${match}</a>`);
  processed = processed.replace(/\n/g, '<br>');
  
  return DOMPurify.sanitize(processed, {
    ALLOWED_TAGS: ['strong', 'em', 'u', 's', 'code', 'a', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
  });
};

const processedContent = computed(() => processMarkdown(props.content));

// ===== CORRIGIDO: Comparação com .trim() APENAS no title =====
const processedItems = computed(() => {
  const clickedNormalized = (clickedValue.value || '').trim();
  
  return items.value.map(item => {
    const titleNormalized = (item.title || '').trim();
    
    return {
      ...item,
      title: processMarkdown(item.title || ''),
      // Comparar APENAS com o title (que é o que vem no content da resposta)
      isClicked: clickedNormalized === titleNormalized
    };
  });
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="input-select">
    <div class="input-select-content">
      <div v-if="content" class="message-content mb-3" v-html="processedContent" />
      
      <div v-if="items.length" class="buttons-container space-y-2">
        <button
          v-for="(item, index) in processedItems"
          :key="index"
          :class="[
            'select-button w-full px-4 py-3 text-left font-medium',
            'transition-all duration-200 ease-out',
            'rounded-xl',
            ...getButtonStateClasses(item.isClicked)
          ]"
          disabled
        >
          <span class="flex-1 flex items-center gap-2" v-html="item.title" />
          

        </button>
      </div>
    </div>
  </BaseBubble>
</template>

<style scoped lang="scss">
.input-select-content {
  @apply flex flex-col gap-3;
}

.message-content {
  @apply text-sm leading-relaxed;
  
  ::v-deep {
    strong { @apply font-semibold; }
    em { @apply italic; }
    u { @apply underline; }
    s { @apply line-through; }
    code { @apply font-mono text-xs; }
    a { @apply hover:underline; }
  }
}

.select-button {
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  ::v-deep strong { @apply font-bold; }
}

/* ===== BOTÃO ELEVADO (não clicado) ===== */
.button-elevated {
  /* Sombra forte para dar elevação */
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.15),
    0 2px 4px -1px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  
  /* Elevar 2px para cima */
  transform: translateY(-2px) scale(1);
}

/* ===== BOTÃO PRESSIONADO (clicado) ===== */
.button-pressed {
  /* Sombra interna para efeito afundado */
  box-shadow: 
    inset 0 2px 4px 0 rgba(0, 0, 0, 0.25),
    inset 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  
  /* Afundar 1px para baixo + reduzir tamanho */
  transform: translateY(1px) scale(0.98);
}

/* Transição suave para dark mode */
.dark {
  .button-elevated {
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.3),
      0 2px 4px -1px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(255, 255, 255, 0.05);
  }
  
  .button-pressed {
    box-shadow: 
      inset 0 2px 4px 0 rgba(0, 0, 0, 0.4),
      inset 0 1px 2px 0 rgba(0, 0, 0, 0.3);
  }
}
</style>